import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Globe, 
  Shield, 
  CheckCircle, 
  AlertTriangle, 
  Copy, 
  ExternalLink,
  Settings,
  Lock,
  Zap,
  Palette
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface CustomDomain {
  id: string;
  domain: string;
  status: 'active' | 'pending' | 'error';
  sslStatus: 'active' | 'pending' | 'error';
  verifiedAt?: string;
  dnsRecords: DNSRecord[];
}

interface DNSRecord {
  type: string;
  name: string;
  value: string;
  status: 'verified' | 'pending' | 'error';
}

interface WhiteLabelSettings {
  customLogo: string;
  brandName: string;
  primaryColor: string;
  secondaryColor: string;
  favicon: string;
  customCSS: string;
  hideInternalBranding: boolean;
  customEmailDomain: string;
}

export const CustomDomainManager: React.FC = () => {
  const { toast } = useToast();
  const [newDomain, setNewDomain] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  const [domains] = useState<CustomDomain[]>([
    {
      id: '1',
      domain: 'bills.acmecorp.com',
      status: 'active',
      sslStatus: 'active',
      verifiedAt: '2024-01-15T10:30:00Z',
      dnsRecords: [
        { type: 'CNAME', name: 'bills', value: 'billing-platform.lovable.app', status: 'verified' },
        { type: 'TXT', name: '_acme-challenge.bills', value: 'verification-token-12345', status: 'verified' }
      ]
    },
    {
      id: '2',
      domain: 'portal.globalenergy.io',
      status: 'pending',
      sslStatus: 'pending',
      dnsRecords: [
        { type: 'CNAME', name: 'portal', value: 'billing-platform.lovable.app', status: 'pending' },
        { type: 'TXT', name: '_acme-challenge.portal', value: 'verification-token-67890', status: 'pending' }
      ]
    }
  ]);

  const [whiteLabel, setWhiteLabel] = useState<WhiteLabelSettings>({
    customLogo: '',
    brandName: 'ACME Corp Billing',
    primaryColor: '#0066cc',
    secondaryColor: '#004499',
    favicon: '',
    customCSS: '',
    hideInternalBranding: true,
    customEmailDomain: 'billing@acmecorp.com'
  });

  const handleAddDomain = async () => {
    if (!newDomain) return;
    
    setIsAdding(true);
    // Simulate API call
    setTimeout(() => {
      toast({
        title: "Domain Added",
        description: `${newDomain} has been added and DNS verification is in progress.`,
      });
      setNewDomain("");
      setIsAdding(false);
    }, 1000);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: "DNS record copied to clipboard",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'verified':
        return 'default';
      case 'pending':
        return 'secondary';
      case 'error':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
      case 'verified':
        return <CheckCircle className="h-4 w-4 text-success" />;
      case 'pending':
        return <AlertTriangle className="h-4 w-4 text-warning" />;
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-destructive" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Custom Domain & White-Label</h1>
          <p className="text-muted-foreground">
            Configure custom domains and white-label branding for your enterprise deployment
          </p>
        </div>
        <Button variant="outline">
          <ExternalLink className="h-4 w-4 mr-2" />
          Documentation
        </Button>
      </div>

      <Tabs defaultValue="domains" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="domains">Custom Domains</TabsTrigger>
          <TabsTrigger value="whitelabel">White-Label Branding</TabsTrigger>
          <TabsTrigger value="security">Security & SSL</TabsTrigger>
        </TabsList>

        <TabsContent value="domains" className="space-y-6">
          {/* Add Domain */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Add Custom Domain
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="domain">Domain Name</Label>
                  <Input
                    id="domain"
                    placeholder="billing.yourcompany.com"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                  />
                </div>
                <div className="flex items-end">
                  <Button onClick={handleAddDomain} disabled={isAdding || !newDomain}>
                    {isAdding ? "Adding..." : "Add Domain"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Domain List */}
          <div className="space-y-4">
            {domains.map((domain) => (
              <Card key={domain.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{domain.domain}</CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge variant={getStatusColor(domain.status)}>
                        {domain.status}
                      </Badge>
                      <Badge variant={getStatusColor(domain.sslStatus)}>
                        SSL: {domain.sslStatus}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-semibold mb-2">Domain Status</h4>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(domain.status)}
                          <span className="text-sm">
                            {domain.status === 'active' ? 'Active and verified' : 
                             domain.status === 'pending' ? 'Verification pending' : 
                             'Verification failed'}
                          </span>
                        </div>
                        {domain.verifiedAt && (
                          <p className="text-sm text-muted-foreground mt-1">
                            Verified: {new Date(domain.verifiedAt).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">SSL Certificate</h4>
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-success" />
                          <span className="text-sm">
                            {domain.sslStatus === 'active' ? 'SSL active' : 'SSL provisioning'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">DNS Records</h4>
                      <div className="space-y-2">
                        {domain.dnsRecords.map((record, index) => (
                          <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">{record.type}</Badge>
                                <span className="font-mono text-sm">{record.name}</span>
                                {getStatusIcon(record.status)}
                              </div>
                              <p className="font-mono text-sm text-muted-foreground mt-1">
                                {record.value}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(record.value)}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="whitelabel" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Branding Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5" />
                  Brand Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="brandName">Brand Name</Label>
                  <Input
                    id="brandName"
                    value={whiteLabel.brandName}
                    onChange={(e) => setWhiteLabel(prev => ({ ...prev, brandName: e.target.value }))}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="primaryColor">Primary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="primaryColor"
                        type="color"
                        value={whiteLabel.primaryColor}
                        onChange={(e) => setWhiteLabel(prev => ({ ...prev, primaryColor: e.target.value }))}
                        className="w-16"
                      />
                      <Input
                        value={whiteLabel.primaryColor}
                        onChange={(e) => setWhiteLabel(prev => ({ ...prev, primaryColor: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="secondaryColor">Secondary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="secondaryColor"
                        type="color"
                        value={whiteLabel.secondaryColor}
                        onChange={(e) => setWhiteLabel(prev => ({ ...prev, secondaryColor: e.target.value }))}
                        className="w-16"
                      />
                      <Input
                        value={whiteLabel.secondaryColor}
                        onChange={(e) => setWhiteLabel(prev => ({ ...prev, secondaryColor: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="customEmail">Custom Email Domain</Label>
                  <Input
                    id="customEmail"
                    placeholder="notifications@yourcompany.com"
                    value={whiteLabel.customEmailDomain}
                    onChange={(e) => setWhiteLabel(prev => ({ ...prev, customEmailDomain: e.target.value }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Hide Internal Branding</Label>
                    <p className="text-sm text-muted-foreground">
                      Remove all references to the internal platform
                    </p>
                  </div>
                  <Switch
                    checked={whiteLabel.hideInternalBranding}
                    onCheckedChange={(checked) => 
                      setWhiteLabel(prev => ({ ...prev, hideInternalBranding: checked }))
                    }
                  />
                </div>
              </CardContent>
            </Card>

            {/* Preview */}
            <Card>
              <CardHeader>
                <CardTitle>Brand Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="p-6 border rounded-lg"
                  style={{ 
                    backgroundColor: whiteLabel.primaryColor + '10',
                    borderColor: whiteLabel.primaryColor + '30'
                  }}
                >
                  <div className="text-center space-y-4">
                    <div 
                      className="text-2xl font-bold"
                      style={{ color: whiteLabel.primaryColor }}
                    >
                      {whiteLabel.brandName}
                    </div>
                    <div className="p-4 bg-background rounded border">
                      <div className="text-sm text-muted-foreground mb-2">Sample Dashboard Header</div>
                      <div 
                        className="h-8 rounded"
                        style={{ backgroundColor: whiteLabel.primaryColor }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div 
                        className="h-16 rounded border"
                        style={{ backgroundColor: whiteLabel.secondaryColor + '20' }}
                      />
                      <div 
                        className="h-16 rounded border"
                        style={{ backgroundColor: whiteLabel.primaryColor + '20' }}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Custom CSS</CardTitle>
              <p className="text-sm text-muted-foreground">
                Add custom CSS to further customize the appearance
              </p>
            </CardHeader>
            <CardContent>
              <textarea
                className="w-full h-32 p-3 border rounded-lg font-mono text-sm"
                placeholder="/* Add your custom CSS here */"
                value={whiteLabel.customCSS}
                onChange={(e) => setWhiteLabel(prev => ({ ...prev, customCSS: e.target.value }))}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  SSL Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">Auto SSL Provisioning</div>
                    <p className="text-sm text-muted-foreground">
                      Automatically provision SSL certificates for new domains
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">Force HTTPS</div>
                    <p className="text-sm text-muted-foreground">
                      Redirect all HTTP traffic to HTTPS
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">HSTS Headers</div>
                    <p className="text-sm text-muted-foreground">
                      Enable HTTP Strict Transport Security
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Performance
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">CDN Acceleration</div>
                    <p className="text-sm text-muted-foreground">
                      Enable global content delivery network
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">Edge Caching</div>
                    <p className="text-sm text-muted-foreground">
                      Cache static assets at edge locations
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">Gzip Compression</div>
                    <p className="text-sm text-muted-foreground">
                      Compress responses for faster loading
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end gap-3">
        <Button variant="outline">Cancel</Button>
        <Button>Save Changes</Button>
      </div>
    </div>
  );
};